import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { useListContainerGroupsQuery } from "~/shared/api/container-group/container-group.grpc";
import { ContainerGroupsBlock } from "~/features/pickup-location-modal/components/container-groups.block";
import { ScheduleBlock } from "~/features/pickup-location-modal/components/schedule.block";
import { LocationPicker } from "./location-picker";
import { StepShell } from "./step-shell";

type Props = {
  providerId?: number;
  /** The customer from step 1 — scopes the picker and owns the groups created here. */
  organizationId?: bigint;
  /** The contract from step 2; new groups are pinned to it. */
  contractId?: bigint;
  /** The site from step 3, created or picked — preselected, and the step stays locked without it. */
  locationId?: bigint;
  /** Which of the two list steps to render; both share this shell. */
  variant: "containerGroups" | "schedule";
};

/** Steps 4 and 5 reuse the pickup-location card's blocks, which save on their own — no footer actions. */
export const LocationListStep: React.FC<Props> = ({
  providerId,
  organizationId,
  contractId,
  locationId,
  variant,
}) => {
  const t = useT();
  const [selectedLocationId, setSelectedLocationId] = React.useState<bigint | undefined>(
    locationId,
  );

  // Follow step 3 when it switches site; a pick here holds until then.
  React.useEffect(() => setSelectedLocationId(locationId), [locationId]);

  // currentData: stale `data` from the previous site would attach a schedule to the wrong one.
  const { currentData, isFetching } = useListContainerGroupsQuery(
    { pickupLocationIds: selectedLocationId != null ? [selectedLocationId] : [] },
    { skip: selectedLocationId == null },
  );
  // Only this customer's groups: a shared site may carry other customers' groups too.
  const containerGroups = React.useMemo(
    () =>
      (currentData?.containerGroups ?? []).filter(
        (group) =>
          organizationId == null || String(group.ownerOrganizationId) === String(organizationId),
      ),
    [currentData, organizationId],
  );

  return (
    <StepShell>
      <div className="flex w-full flex-col gap-8">
        <LocationPicker
          providerId={providerId}
          value={selectedLocationId}
          onChange={setSelectedLocationId}
          label={t(Namespace.Customers, "onboarding.locationLabel")}
        />

        {selectedLocationId == null ? (
          <p className="text-sm text-icon-secondary">
            {t(Namespace.Customers, "onboarding.pickLocationFirst")}
          </p>
        ) : variant === "containerGroups" ? (
          <ContainerGroupsBlock
            locationId={String(selectedLocationId)}
            organizationId={providerId}
            containerGroups={containerGroups}
            isLoading={isFetching}
            lockedOrganizationId={organizationId != null ? String(organizationId) : undefined}
            lockedContractId={contractId != null ? String(contractId) : undefined}
          />
        ) : (
          <ScheduleBlock
            locationId={String(selectedLocationId)}
            containerGroups={containerGroups}
          />
        )}
      </div>
    </StepShell>
  );
};
